import { css, html, LitElement, nothing, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { getAlternateLinks, UmbracoPage } from '../ContentLoader.ts'
import { StateSwitcher } from '../Utils/StateSwitcher.ts'
import './../Shared/Icons/Icon.ts'
import { Flags } from '../Shared/Flags/Flag.ts'
import Logo from './Logo.svg?raw'
import styles from './Header.css?raw'
import { UmbNavItem } from '../api/UmbNavItem.ts'
import { sortBy } from '../SortBy.ts'
import { translate } from '../Shared/Translations.ts'



type MenuMode = 'items' | 'icons'

@customElement('lindberg-header')
export class Header extends LitElement {
	@property({ attribute: false })
	private _root!: UmbracoPage
	public get root(): UmbracoPage {
		return this._root
	}
	public set root(value: UmbracoPage) {
		this._root = value
		const flatten = (value: UmbracoPage): UmbracoPage[] => value.children?.reduce<UmbracoPage[]>((acc, child) => acc.concat(child, flatten(child)),[value]) ?? [value]
		const pages = flatten(value)		
		// console.log(pages)
		
		function toNavItem(item: UmbNavItem): UmbNavItem {
			return {
				...item,
				showInHorizontalMenu: !item.customClasses?.includes('vertical'),
				showInVerticalMenu: !item.customClasses?.includes('horizontal'),
				children: item.children ? toNavItems(item.children) : null,
				page: pages.find(x => x.id == item.contentKey)				
			}
		}

		// Drop placeholder links to "#", unless they open a submenu or the language picker
		function toNavItems(items: UmbNavItem[]): UmbNavItem[] {
			return items.map(toNavItem).filter(x => x.url != '#' || x.children?.length || x.itemType == 'language')
		}

		this.items = toNavItems(value.properties.headerItems as UmbNavItem[])
		this.icons = toNavItems(value.properties.headerIcons as UmbNavItem[])
	}

	@property({ attribute: false })
	currentPage!: UmbracoPage

	// @property({ attribute: false })
	// breadcrumb: UmbracoPage[]

	@query('header')
	header!: HTMLElement

	@state()
	items: UmbNavItem[] = []

	@state()
	icons: UmbNavItem[] = []

	@state()
	vertical = false

	@state()
	compact = false

	@state()
	private _menuOpen = false
	public get menuOpen() {
		return this._menuOpen
	}
	public set menuOpen(value) {
		this._menuOpen = value
		if(value && this.vertical){
			document.body.style.overflow = 'hidden'

		} else {
			document.body.style.overflow = ''
		}
	}

	verticalBreak: number = 0

	/* Last layout mode we rendered; undefined until the first update. */
	private wasVertical?: boolean

	static styles = [
		unsafeCSS(styles),

		css`:host([debug]) header {
			> a.logo {
				outline: 2px solid deeppink;
				outline-offset: -1px;
			}
			> nav.menu-items {
				outline: 2px solid deepskyblue;
				outline-offset: -1px;
				> * {
					outline: .5px dashed gray;
				}
			}
			> nav.menu-icons {
				outline: 2px solid limegreen;
				outline-offset: -1px;
				> * {
					outline: .5px dashed gray;
				}
			}
		`
	]

	firstUpdated() {
		window.addEventListener('resize', this.updated.bind(this))

		const range = 88
		let scrollPosition = window.scrollY
		const header = this.header
		const scrollState = new StateSwitcher(header, 'header-animation', 'compact')

		window.addEventListener(
			'scroll',
			e => {
				if(this.items.find(x => x.open) || this.icons.find(x => x.open)) return
				if (this.menuOpen) return
				const currentScroll = window.scrollY
				const value = Math.min(1, Math.max(0, (currentScroll - scrollPosition) / range))

				scrollState.input(value)
				scrollPosition = Math.max(Math.min(scrollPosition, currentScroll + range), currentScroll - range)

				const components = getElementsFromPointRecursive(this.offsetLeft + 16, 44).toReversed()
				this.header.style.setProperty('--background-color-scheme', components.find(x => x.style.colorScheme && x.tagName != 'LINDBERG-HEADER')?.style.colorScheme || 'light')
			},
			{ passive: true }
		)

		document.body.addEventListener('click', (e: MouseEvent) => {
			const submenuOpen = this.items.some(x => x.open) || this.icons.some(x => x.open)
			const outsideClick = !getElementsFromPointRecursive(e.clientX, e.clientY).includes(this)
			if (!this.vertical && submenuOpen && outsideClick){
				this.items.forEach(x => x.open = false)
				this.icons.forEach(x => x.open = false)
				this.requestUpdate()
				console.log('Closing menu because click occurred outside header.')
			}
		})

		this.header.querySelectorAll<HTMLAnchorElement>('nav.menu-items > a, nav.menu-items > div > button, nav.menu-items > div > a').forEach((a) =>
			a.addEventListener('mouseenter', () => {
				this.header.style.setProperty('--underline-offset', a.offsetLeft + 'px')
				this.header.style.setProperty('--underline-width', a.offsetWidth + 'px')
			})
		)
		this.header.querySelectorAll<HTMLAnchorElement>('nav.menu-items > a, nav.menu-items > div > button, nav.menu-items > div > a').forEach(a =>
			a.addEventListener('mouseleave', () => {})
		)
	}

	private renderNav(items: UmbNavItem[], mode: MenuMode) {
		const cultures = sortBy(Object.keys(this.currentPage?.cultures || {}), x => x == "en" ? 0 + x : ["ko", "ja", "zh"].includes(x) ? 2 + x : 1 + x)
		const alternateLinks = getAlternateLinks(this.currentPage)
		const ariaCurrent = (url?: string | null) => url && url == this.currentPage?.route?.path ? 'page' : nothing
		return items?.filter(x => this.vertical ? x.showInVerticalMenu : x.showInHorizontalMenu).map((x, index) => {
			if (x.itemType == 'divider') return html`<div aria-hidden="true"></div>`

			const isIcons = mode == 'icons'
			const icon = html`<lindberg-icon aria-hidden="true">${unsafeSVG(x.image)}</lindberg-icon>`
			const label = x.title || x.page?.name || x.name
			const subId = `sub-${mode}-${x.key.split("-").at(0)}`

			const href = x.url
			/* console.log(x, label, href) */

			if(x.itemType == "language"){
				/* const currentPage = this.breadcrumb?.at(-1) */
				return html`<div>
					<button @click=${() => this.toggleSub(x)} aria-label=${isIcons ? label : nothing} aria-expanded=${x.open ? 'true' : 'false'} aria-controls=${subId}>${isIcons ? icon : label}</button>
					<nav class="sub" id=${subId} aria-label=${label} ?open=${x.open}>
						${Object.entries(alternateLinks).map(([lang, url]) => html`
							<a href=${url}>
							<span aria-hidden="true">${unsafeSVG(Flags[lang.toUpperCase()])}</span>
							${new Intl.DisplayNames([lang], { type: 'language' }).of(lang)?.toLowerCase()}</a>`)}
					</nav>
				</div>`
			}
			if (x.children?.length) {
				return html`<div>
					<button @click=${() => this.toggleSub(x)} aria-expanded=${x.open ? 'true' : 'false'} aria-controls=${subId}>${label} ${x.open ? html`<lindberg-icon xmark aria-hidden="true"></lindberg-icon>` : html`<lindberg-icon angle-down aria-hidden="true" ></lindberg-icon>`}</button>
					<nav class="sub" id=${subId} aria-label=${label} ?open=${x.open}>
						${x.children.map(
							y => y.itemType == "divider" ? html`<div aria-hidden="true"></div>` : html`<a href=${y.url} target=${y.target || nothing} aria-current=${ariaCurrent(y.url)}>${y.title || y.page?.name || y.name}</a>`
						)}
					</nav>
				</div>`
			}
			return html`<a href=${href} target=${x.target || nothing} aria-label=${isIcons ? label : nothing} aria-current=${ariaCurrent(href)}>${isIcons ? icon : label}</a>`
		})
	}

	render() {
		/* const currentPage = this.breadcrumb?.at(-1) */

		return html`
			<a href="#" class="skip-link" @click=${this.skipToContent}>${translate('skip-to-content')}</a>
			<header ?vertical=${this.vertical} ?menu-open=${this.menuOpen} ?compact=${this.compact}>
				<a href=${this.root?.route?.path || '#'} class="logo" aria-label=${translate('home')}>${unsafeSVG(Logo)}</a>

				<nav class="menu-items" id="menu-items" aria-label=${translate('nav-main')}>
					${this.renderNav(this.items, 'items')}
				</nav>

				<nav class="menu-icons" aria-label=${translate('nav-utility')}>
					${this.renderNav(this.icons, 'icons')}
					${this.vertical ?
						html`<button class="menu-toggle" @click=${() => (this.menuOpen = !this.menuOpen)} aria-label=${translate(this.menuOpen ? 'menu-close' : 'menu-open')} aria-expanded=${this.menuOpen ? 'true' : 'false'} aria-controls="menu-items">
							${this.menuOpen ? html`<lindberg-icon xmark aria-hidden="true"></lindberg-icon>` : html`<lindberg-icon bars aria-hidden="true"></lindberg-icon> `}
						</button>`
					: nothing}
				</nav>
			</header>
		`
	}

	/* The page sections live in another shadow root, so a fragment link can't reach them.
	   Move focus to the first element after the header instead. */
	skipToContent(e: Event) {
		e.preventDefault()
		const content = this.nextElementSibling as HTMLElement | null
		if (!content) return
		if (!content.hasAttribute('tabindex')) content.setAttribute('tabindex', '-1')
		content.focus({ preventScroll: true })
		content.scrollIntoView()
	}

	toggleSub(item: UmbNavItem) {
		item.open = !item.open
		;[this.items, this.icons].flat().filter(x => x != item).forEach(x => (x.open = false))
		this.requestUpdate()
	}

	updated() {
		if (this.header.scrollWidth > this.clientWidth) {
			this.verticalBreak = this.header.scrollWidth
			/* console.log('setting vertical', this.header.scrollWidth, this.clientWidth) */
		}
		this.vertical = this.verticalBreak > this.clientWidth

		/* Switching layout mode (and the first paint) changes which menu is shown at all,
		   so skip the transitions for that frame instead of animating the swap. */
		if (this.wasVertical !== this.vertical) {
			this.wasVertical = this.vertical
			this.header.style.setProperty('--transition', '0s')
			requestAnimationFrame(() => requestAnimationFrame(() => this.header.style.removeProperty('--transition')))
		}

		if (!this.vertical) {
			/* console.log(this.offsetLeft) */
			const subs = Array.from(this.header.querySelectorAll<HTMLElement>('.sub[open]'))

			subs.forEach(sub => {
				const maxLeft = this.header.offsetLeft + this.header.offsetWidth - sub.offsetWidth - parseInt(getComputedStyle(this).getPropertyValue('--space-500'))
				const left = Math.min(this.header.offsetLeft + (sub.parentElement?.offsetLeft ?? 0), maxLeft)
				sub.style.left = left + 'px'
			})

			const dropDownHeight = subs.length ? `calc(${Math.max(...subs.map(x => x.scrollHeight))}px + var(--space-900))` : '0px'
			this.style.setProperty('--dropdown-height', dropDownHeight)

			this.header.querySelectorAll<HTMLElement>('nav.menu-items > *').forEach(item => {
				item.style.transform = ''
			})
		} else {
			/* console.log('Updating header for mobile') */
			this.style.setProperty('--dropdown-height', this.menuOpen ? '100dvh' : '0px')

			this.header.querySelectorAll<HTMLElement>('nav.menu-items > *').forEach(item => {
				item.style.transform = ''
			})
			this.header.querySelectorAll<HTMLElement>('nav.menu-items .sub').forEach(sub => {
				sub.style.left = ''
			})
		}
	}
}

function getElementsFromPointRecursive(x: number, y: number) {
	const elements = document.elementsFromPoint(x, y).toReversed() as HTMLElement[]
	const result = [] as HTMLElement[]

	function processElements(elementsList: HTMLElement[]) {
		elementsList.filter(x => !result.includes(x) && x.shadowRoot).forEach(el => {
			result.push(el)
			processElements((el.shadowRoot?.elementsFromPoint(x, y) ?? []) as HTMLElement[])
		})
	}

	processElements(elements)

	return result
}